import { NgClass, NgTemplateOutlet } from '@angular/common'
import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core'
import { delay, from, Subject, takeUntil } from 'rxjs'

import {
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  headShakeAnimation,
} from 'angular-animations'

import Keyboard from 'simple-keyboard'

import {
  SafePipe,
  PlayButtonComponent,
  SlidePanelComponent
} from '@nx-superprep/shared'

import { gameplaySpacing, log } from '@nx-superprep/utils'

import { FrontendCard as Card, Choice, ChoiceType, PartialChoice } from '@nx-superprep/backend/firebase'

import { GameplayBaseComponent, gameplayBaseStyling, gameplayReplace } from '../gameplay-base'
import { MatCardModule } from '@angular/material/card'
import { flatMap, join, uniq } from 'lodash-es'

class NumberElement extends PartialChoice<string> {
  override get displayValue() {
    return (this.value ?? '') + (this.isComplete
      ? gameplaySpacing.smallSpacer
      : gameplayBaseStyling.smallEllipsis.repeat(this.availableCount))
  }
  override get displayAnswer() {
    return gameplaySpacing.smallSpacer + (this.answer ?? '') + gameplaySpacing.smallSpacer
  }
}

@Component({
  standalone: true,
  selector: 'gameplay-numbers',
  templateUrl: './numbers.component.html',
  styleUrls: ['./numbers.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    fadeInOnEnterAnimation({ duration: 0 }),
    fadeOutOnLeaveAnimation({ duration: 900, delay: 0 }),
    headShakeAnimation({ duration: 900, delay: 0 }),
  ],
  imports: [
    NgClass, NgTemplateOutlet,
    SafePipe,
    MatCardModule,
    SlidePanelComponent, PlayButtonComponent
  ],
})
export class NumbersComponent extends GameplayBaseComponent implements OnDestroy, AfterViewInit {
  @Output() confirmChoice = new EventEmitter<string>()
  @ViewChild('answerInput', {static: false}) answerInput: ElementRef | undefined

  override title = 'numbers'

  keyboard: Keyboard | undefined
  inputValue = ''
  isMultiple = false
  isSingleDigit = true

  private readonly animationDuration = 900
  private readonly onEnter = new Subject<void>()

  private _selectedElements: NumberElement[] = []
  private _animationCount = 0

  keyboardOptions = {
    onChange: (input: string) => this.onChange(input),
    onKeyPress: (button: string) => this.onKeyPress(button),
    physicalKeyboardHighlightPress: true,
    physicalKeyboardHighlight: true,
    physicalKeyboardHighlightTextColor: 'white',
    physicalKeyboardHighlightBgColor: 'rgb(91, 130, 235)',
    syncInstanceInputs: true,
    mergeDisplay: true,
    theme: 'hg-theme-default hg-layout-numeric numeric-theme',
    layoutName: 'numbers',
    layout:{
      numbers: [
        '{numpad7} {numpad8} {numpad9}',
        '{numpad4} {numpad5} {numpad6}',
        '{numpad1} {numpad2} {numpad3}',
        '{numpad0} {numpaddecimal} {backspace}',
      ],
      numbersOnly: [
        '{numpad7} {numpad8} {numpad9}',
        '{numpad4} {numpad5} {numpad6}',
        '{numpad1} {numpad2} {numpad3}',
        '{numpad0} {backspace}',
      ],
      chemFormula: [
        '{numpad5} {numpad6} {numpad7}',
        '{numpad2} {numpad3} {numpad4}',
        '{numpadplus} {numpad1} {numpadminus}',
        '{backspace}',
      ]
    },
    display: {
      '{numbers}': '123',
      '{ent}': 'return',
      '{escape}': 'esc ⎋',
      '{tab}': 'tab ⇥',
      '{backspace}': '⌫',
      '{capslock}': 'caps lock ⇪',
      '{shift}': '⇧',
      '{controlleft}': 'ctrl ⌃',
      '{controlright}': 'ctrl ⌃',
      '{altleft}': 'alt ⌥',
      '{altright}': 'alt ⌥',
      '{metaleft}': 'cmd ⌘',
      '{metaright}': 'cmd ⌘',
      '{abc}': 'ABC',
      '{numpaddecimal}': '.',
      '{comma}': ',',
      '{numpadplus}': '+',
      '{numpadminus}': '-',
      '{numpadenter}': '↵',
    },
    inputMask: {
      default: {
        mask: '+1 (999) 999-9999',
        regex: /^[0-9]+$/
      },
      chemistry: {
        mask: '+1 (999) 999-9999',
        regex: /^[1-7+-]+$/  // Only allow numbers 0-7, + and -
      }
    }
  }

  buttons = join(uniq(flatMap(this.keyboardOptions.layout, rows => flatMap(rows, row => row.split(' ')))),' ')

  getLayoutName() {
    return this.isChemFormulaKeyboard ? 'chemFormula': this.isNumberOnlyKeyboard ? 'numbersOnly' : 'numbers'
  }

  get isChemFormulaKeyboard() {
    return this.answer?.tuple?.some(val => ['+', '-'].includes(val as string)) ?? false
  }
  get isNumberOnlyKeyboard() {
    return !(this.answer?.tuple?.some(val => ['.', ','].includes(val as string)) ?? false)
      || (this.answer?.name.split('').some(val => ['.', ','].includes(val as string)) ?? false)
  }

  onAnimationStart(e: { triggerName: string; }) {
    //this.keyboard?.addButtonTheme(`{numpadenter}`, 'hidden');
    if (e.triggerName === 'headShake') {
      this._animationCount = this.isMultiple ? 2 : 1;
    } else if (e.triggerName === 'fadeOutOnLeave') {
      this._animationCount = 1;
    }
  }

  override onAnimationDone(e: { triggerName: string; }) {
    this._animationCount--;
    // Ensure we're tracking the correct sequence
    if (this._animationCount <= 0 && e.triggerName === 'fadeOutOnLeave') {
      // Reset the counter for fadeInOnEnter
      this._animationCount = 0;
    }
  }

  override afterSelect(choice: Choice | undefined): void {
    if (choice) {
      this.updateKeyboardTheme(choice);
    }
  }

  private updateKeyboardTheme(choice: Choice) {
    if (!this.isMultiple) {
      this.keyboard?.addButtonTheme(`{numpad${this.answer?.name}}`, "correct");
    }
  }

  override get shouldShowPlayButton() {
    return Boolean(!this.shouldShowQuestionText && this.question?.hasAudio)
  }

  get shouldShowSubmitButton() {
    return false // this.isAllValuesFilled && !this.didAnswer
  }

  get isAnimationDone() {
    return this._animationCount <= 0
  }

  get shouldShowQuestionText() {
    return (this.question?.hasImage || this.question?.hasText) && !this.question.hasAudio && !this.questionHasEllipsis
  }

  get questionHasEllipsis() {
    return (this.questionText() as string).match(gameplayBaseStyling.ellipsis)
  }

  get selectedValues() {
    return this._selectedElements.filter(e => e.value ).map(e => e.value)
  }

  get isAnswer() {
    return this._selectedElements.every(e => e.isCorrect)
  }

  get isPrefix() {
    return this._selectedElements.every(e => e.isPrefix)
  }

  get isAllValuesFilled() {
    return this._selectedElements.every(e => e.isComplete)
  }

  get answerValue() {
    return this.answer?.textUrl
  }

  resetKeyboard() {
    if (this.keyboard) {
      this.keyboard.clearInput()
      this.keyboard.setOptions({layoutName:this.getLayoutName()})
      //this.keyboard.addButtonTheme(`{numpadenter}`, 'hidden')
      this.keyboard.removeButtonTheme(this.buttons,'correct')
    }
  }

  override getData() {
    super.getData()
    from(this.onEnter).pipe(
      takeUntil(this.onDestroy),
      delay(500),
    ).subscribe(() => this.handleEnter())
  }

  override initState(card: Card | undefined) {
    if (!super.initState(card)) { return false }

    this._animationCount = 1 // Set initial animation count
    this._selectedElements = []
    this.inputValue = ''
    this.resetKeyboard()

    // Reset component state after animation completes
    setTimeout(() => {
      if (!this.question) { return }
      if (!this.answer) { return }
      this.isMultiple = !this.question.hasImage && (this.answer.isMultiple || this.answer.isTuple)
      this.isSingleDigit = (this.isMultiple && this.answer.count === 1) || this.answer.name.length === 1
      this._selectedElements = ((
        this.isMultiple ? this.answer.tuple : [this.answer?.textUrl]
      ) as string[]).map((e: string) => new PartialChoice<string>({choice: e, answer: e, value: '', length: e.length }))
      this._animationCount = 0
    }, this.animationDuration)

    return true
  }

  override resetState(card: Card | undefined) {
    super.resetState(card)
    this._animationCount = 0
    this._selectedElements.forEach(e => e.reset())
    this.inputValue = ''
    this.resetKeyboard()
    this.answerInput?.nativeElement?.focus()
  }

  upsert(value: string) {
    const found = this._selectedElements.findIndex(e => !e.isComplete)
    if (found < 0) { return; }
    const element = this._selectedElements[found]
    element.value = value
    this.inputValue = this.isMultiple && element.isComplete && (found + 1) < this._selectedElements.length
      ? ''
      : element.value
    this.keyboard?.setInput(this.inputValue)
    log.trace('upsert', value, this.keyboard?.getInput())
  }

  getCompound(text: string, elements: NumberElement[], isCorrect: boolean) {
    const _l = elements.length
    const _style = isCorrect ? gameplayBaseStyling.answerRightStyle : gameplayBaseStyling.answerWrongStyle
    return _l > 0 && !!text.match(gameplayBaseStyling.ellipsis)
    ? text
      .split(gameplayBaseStyling.ellipsis)
      .map((s, i) => s + (i < _l ? isCorrect ? elements[i].displayAnswer : elements[i].displayValue : ''))
      .join('')
      .replace(gameplayReplace.answerStyle, _style)
    : text
  }

  //? temp fix for single digit spacing issue

  override questionText() {
    let text = super.question?.textUrl.replace(gameplayReplace.ell, '').replace(gameplayReplace.punctuation, '').length !== 0 ? super.questionText() : ''
    text = (this.answer?.tuple ?? []).length > 1 ? text : (text as string).replaceAll(gameplayReplace.spacers, '')
    return text
  }

  override answerText() {
    let text = this.getCompound(super.bottomText() as string, this._selectedElements, true)
    text = (this.answer?.tuple ?? []).length > 1 ? text : text.replaceAll(gameplayReplace.spacers, gameplaySpacing.smallSpacer)
    return text
  }

  override bottomText() {
    let text = this.getCompound(super.bottomText() as string, this._selectedElements, this.isAnswer)
    text = this.didAnswer
    ? text.replaceAll(gameplayReplace.spacers, '')
    : text.replaceAll(gameplayBaseStyling.ellipsis, gameplayBaseStyling.smallEllipsis)
    return text
  }

  ngAfterViewInit() {
    log.debug(this.answerInput?.nativeElement)
    if (window) {
      import("simple-keyboard").then(KeyboardClass => {
        const Keyboard = KeyboardClass.default
        this.keyboard = new Keyboard(this.keyboardOptions)
      })
    } else {
      this.keyboard = new Keyboard(this.keyboardOptions)
    }
    this.resetKeyboard()
  }

  override ngOnDestroy() {
    this.keyboard?.destroy()
    // Complete the onEnter subject to free resources
    this.onEnter.complete();
    super.ngOnDestroy();
  }

  onChange = (input: string) => {
    if (this.didAnswer || this.isAllValuesFilled) { return }
    log.debug('onChange', input)
    this.upsert(input)
    if (this.shouldShowSubmitButton) {
      //this.keyboard?.removeButtonTheme(`{numpadenter}`, 'hidden')
    } else {
      //this.keyboard?.addButtonTheme(`{numpadenter}`, 'hidden')
      if (this.shouldSubmitAnswer) { this.onEnter.next() }
    }
  }

  get shouldSubmitAnswer() {
    return this.isAllValuesFilled // when all values are filled
    // return (this.selectedValues.length > 0 && (this.isAnswer || !this.isPrefix))  // on first error
  }

  onKeyPress = (button: string) => {
    if (this.didAnswer) { return; }
    log.debug('onKeyPressed', button)

    if (button === '{shift}' || button === '{lock}') {
      this.handleShift()
    } else if (['{enter}', '{numpadenter}'].includes(button)) {
      this.handleEnter()
    } else if (button === '{backspace}') {
      this.handleBackspace()
    } else if (button === '{numpadplus}') {
      this.handleSpecialKey('+')
    } else if (button === '{numpadminus}') {
      this.handleSpecialKey('-')
    } else {
      this.keyboard?.setInput(this.keyboard.getInput())
    }
  }

  handleSpecialKey(key: string) {
    if (!this.isChemFormulaKeyboard) return

    const currentInput = this.keyboard?.getInput() ?? ''
    const newInput = currentInput + key
    this.keyboard?.setInput(newInput)
    this.onChange(key)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange = (event: any) => {
    log.debug('onInputChange', event)
    this.onChange(event.target.value)
  }

  handleShift = () => {
    const currentLayout = this.keyboard?.options.layoutName
    const shiftToggle = currentLayout === 'default' ? 'shift' : 'default'

    this.keyboard?.setOptions({
      layoutName: shiftToggle,
    })
  }

  handleEnter = () => {
    const _choice = this.isAnswer
      ? this.answer
      : new Choice({
          type: ChoiceType.wrong,
          name: this.selectedValues.join('|'),
        })
    this.onSelect(_choice)
  }

  handleBackspace() {
    let found = this._selectedElements.findIndex(e => e.isDirty)
    if (found < 0) {
      found = this._selectedElements.length - 1 - this._selectedElements.slice().reverse().findIndex(e => e.isComplete)
    }
    if (found < 0 || found >= this._selectedElements.length) { return; }
    const foundValue = this._selectedElements[found].value
    if (!foundValue) { return; }
    const value = foundValue.slice(0, -1)
    this._selectedElements[found].value = value.length > 0 ? value : null
    this.keyboard?.setInput(value)
    this.onChange(value)
  }

  // Implement lifecycle methods (already enforced by GameplayBaseComponent)
  override onPresentQuestion(): void {
    log.debug(`${this.title}: Presenting question...`);
  }

  override onAnswerQuestion(choices: string[]): void {
    log.debug(`${this.title}: Answer selected:`, choices);
  }

  override onPresentAnswer(): void {
    log.debug(`${this.title}: Revealing answer...`);
  }

  override onFinishCard(): void {
    log.debug(`${this.title}: Finishing card...`);
  }

  override onForceAnswer(isCorrect = Math.random() < 0.5) {
    if (isCorrect) { this._selectedElements.forEach(e => e.forceAnswer(true)) }
    this.inputValue = this.selectedValues.join('')
    this.onEnter.next()
  }
}
